import type { AxiosResponse } from 'axios';

import type {
  IMainRequests,
  IRequestHead,
  ItemOfferToday,
  ItemUnderRequest,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import api from '@/utils/axiosInterceptor.ts';
import type { ApiResponse } from '@/utils/deepmerge.ts';

// Top suppliers interface
export interface SupplierStat {
  _id: string; // MongoDB ID of the client reference
  clientID: number; // SQL ID of the client/supplier
  clientName: string; // Name of the client/supplier
  count: number; // Number of requests
  totalAmount: number; // Total monetary value of all requests
}

export interface TopSuppliersResponse {
  allTime: SupplierStat[]; // Top suppliers across all time
  thisMonth: SupplierStat[]; // Top suppliers for the current month
  totalSuppliersWithRequests: number; // Total unique suppliers that have requests
}

// Purchase request interface
export interface PurchaseRequest {
  _id: string;
  Client: {
    _id: string;
    ID: number;
    suppliarClass: string;
  };
  ClientID: number;
  RequestTotal: number;
  RequestItemsCount: number;
  TrDate: string;
  RequestType: string;
  User: {
    _id: string;
    empName: string;
    avatar: string;
  };
  Offer: string;
  HealthyRequestID: number;
  HealthyIncomeID: number;
  IsDelivered: boolean;
  IsCancel: boolean;
  Notes: string;
  Status: number;
  ApprovalUser: string | null;
  ApprovalDate: string | null;
  ApprovalNotes: string;
  InvoicePdfFile: string | null;
  ReturnPdfFile: string | null;
  __v: number;
  clientName: string;
}

export async function GetMainPurchasesRequestsApi() {
  try {
    const response: AxiosResponse<IMainRequests> = await api.get(
      `/purchases-requests/get-main-request`,
    );
    return response?.data;
  } catch (error) {
  }
}

export async function GetRequestDayApi(day: string) {
  try {
    const response: AxiosResponse<PurchaseRequest[]> = await api.get(
      `/purchases-requests/get-day-requests/${day}`,
    );
    return response?.data;
  } catch (error) {
  }
}

export async function GetPendingRequestApi() {
  try {
    const response: AxiosResponse<IRequestHead[]> = await api.get(
      `/purchases-requests/get-pending-requests`,
    );
    return response?.data;
  } catch (error) {
  }
}

export async function GetRequestsHeadOneSuppliar(id: string) {
  try {
    const response: AxiosResponse<ApiResponse<IRequestHead[]>> = await api.get(
      `/purchases-requests/get-suppliar-requests/${id}`,
    );
    return response?.data;
  } catch (error) {
  }
}

export async function GetTopSuppliersApi(limit: number = 10) {
  try {
    const response: AxiosResponse<ApiResponse<TopSuppliersResponse>> =
      await api.get(`/purchases-requests/top-suppliers?limit=${limit}`);
    return response?.data;
  } catch (error) {
  }
}

export async function GetAllPurchaseRequestsApi() {
  try {
    const response: AxiosResponse<ApiResponse<PurchaseRequest[]>> =
      await api.get(`/purchases-requests/all-requests`);
    return response?.data;
  } catch (error) {
    return { data: [], message: 'Failed to fetch all purchase requests' };
  }
}

export async function GetPendingApprovalRequestsApi() {
  try {
    const response: AxiosResponse<ApiResponse<PurchaseRequest[]>> =
      await api.get('/purchases-requests/pending-approval-requests');
    return response?.data;
  } catch (error) {
  }
}

export async function GetActiveRequestsApi() {
  try {
    const response: AxiosResponse<ApiResponse<PurchaseRequest[]>> =
      await api.get('/purchases-requests/active-requests');
    return response?.data;
  } catch (error) {
    return { data: [], message: 'Failed to fetch active requests' };
  }
}

export async function GetCanceledRequestsApi() {
  try {
    const response: AxiosResponse<ApiResponse<PurchaseRequest[]>> =
      await api.get('/purchases-requests/canceled-requests');
    return response?.data;
  } catch (error) {
    return { data: [], message: 'Failed to fetch canceled requests' };
  }
}

/**
 * Fetches all offers for a specific item from today
 * @param itemID - The healthy item ID to fetch offers for
 * @returns A promise that resolves to an array of ItemOfferToday objects
 */
export async function GetItemOffersToday(
  itemID: number,
): Promise<ItemOfferToday[]> {
  try {
    const response: AxiosResponse<ApiResponse<ItemOfferToday[]>> =
      await api.get(`/purchases-requests/item-offers-today/${itemID}`);

    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching item offers:', error);
    return [];
  }
}

/**
 * Fetches all active requests containing a specific item
 * @param itemID - The healthy item ID to fetch active requests for
 * @returns A promise that resolves to an array of ItemUnderRequest objects
 */
export async function GetItemUnderRequest(
  itemID: number,
): Promise<ItemUnderRequest[]> {
  try {
    const response: AxiosResponse<ApiResponse<ItemUnderRequest[]>> =
      await api.get(`/purchases-requests/item-under-request/${itemID}`);

    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching item under request:', error);
    return [];
  }
}
